import React, { forwardRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Accordion } from '@mobi/component-library/Base/Accordion'
import { Card } from '@mobi/component-library/Base/Card'
import type { BetSlipItem } from '@mobi/betslip/types'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import {
  selectActiveInvestment,
  selectIsBettingSectionExpanded,
  selectBonusBetBalance,
  selectBonusCashBalance,
  selectCampaigns,
  selectWorkflowStatus,
} from '@mobi/betslip/Store/Workflow/selectors'
import {
  selectMultiBetError,
  selectMultiBetReceipt,
  selectMultiInvestment,
} from '@mobi/betslip/Store/Bets/selectors'
import { calculateMultiProjectedPay } from '@mobi/betslip/helpers/calculator/multis'
import {
  getBetsInMulti,
  hasEnoughItemsToCreateValidMulti,
  isSelectionAllowedInMulti,
  isValidMulti,
  isValidMultiInvestmentForLegs,
} from '@mobi/betslip/helpers/state'
import { MAX_LEGS_FOR_MULTI_FORMULA, MIN_LEGS_IN_MULTI } from '@mobi/betslip/helpers/constants'
import { useBonusAppliedItems } from '@mobi/betslip/hooks/useBonusAppliedItems'
import { Investment, InvestmentsWrapperStyled } from '../Common/Investment'
import { ShareMyBet } from '../Common/ShareMyBet'
import { InlineAlert } from '../Common/InlineAlert'
import { ReceiptNumber } from '../Common/ReceiptNumber'
import { BetCard } from '../Common/BetCard'
import { ComboMultis } from './Components'
import { MultiItems } from './Components/MultiItems'
import { BonusBetAndCash } from '../Common/BonusBetAndCash'
import { BonusBetInvestment } from '../Common/BonusBetInvestment'
import { setBettingSectionExpandedState } from '@mobi/betslip/Store/Workflow'

export const Multi = forwardRef<HTMLDivElement, { items: BetSlipItem[] }>(({ items }, ref) => {
  const dispatch = useDispatch()

  const workflowStatus = useAppSelector(selectWorkflowStatus)
  const isBusy = useAppSelector(state => state.betslip.workflow.isBusy)
  const activeInvestment = useAppSelector(selectActiveInvestment)
  const receipt = useAppSelector(selectMultiBetReceipt)
  const multiBetError = useAppSelector(selectMultiBetError)
  const multiInvestment = useAppSelector(selectMultiInvestment)
  const isBettingSectionExpanded = useAppSelector(selectIsBettingSectionExpanded)

  const campaigns = useSelector(selectCampaigns)

  const multiItems = items.filter(item => isSelectionAllowedInMulti(item.selection))

  const { remainingBonusCashCampaigns, betSlipMultiInvestment } = useBonusAppliedItems(multiItems)

  const maxAppliedBonusForPlacedMulti =
    workflowStatus === 'bets-placed'
      ? receipt?.bonusBet
      : (remainingBonusCashCampaigns.find(campaign => campaign.remainingAmount > 0)
          ?.remainingAmount ?? 0)

  const hasEnoughMultiItems = hasEnoughItemsToCreateValidMulti(multiItems)
  const selectedMultiItems = getBetsInMulti(items)

  const isMultiValid = isValidMulti(multiInvestment, multiBetError, selectedMultiItems)
  const hasValidMultiInvestment = isValidMultiInvestmentForLegs(
    multiInvestment,
    selectedMultiItems.length
  )

  const isEditable = workflowStatus === 'ready'

  const bonusCashBalance = useAppSelector(selectBonusCashBalance)
  const bonusBetBalance = useAppSelector(selectBonusBetBalance)
  const isBonusFundsAvailable = bonusCashBalance > 0 || bonusBetBalance > 0

  if (!hasEnoughMultiItems || (!isEditable && !isMultiValid && !multiBetError)) return null

  const isDisabled = !isEditable || isBusy || receipt != null

  const hasAllowableMultiCountForCombos =
    selectedMultiItems.length >= MIN_LEGS_IN_MULTI &&
    selectedMultiItems.length <= MAX_LEGS_FOR_MULTI_FORMULA

  const shouldRenderMultiCombosInputs =
    isEditable && hasAllowableMultiCountForCombos && !multiInvestment.isBonusBet

  return (
    <Accordion
      title='Multi (1)'
      isExpandedControlled={isBettingSectionExpanded.Multi}
      shouldStartExpanded={isBettingSectionExpanded.Multi}
      onToggle={isExpandedPrev => {
        dispatch(setBettingSectionExpandedState({ Multi: !isExpandedPrev }))
      }}
      theme={null}
      ref={ref}
    >
      <Card
        topComponent={
          receipt && <InlineAlert type='success' text='Your multi bet has been placed' />
        }
      >
        <MultiItems
          multiItems={multiItems}
          isEditable={isEditable}
          hasValidMultiInvestment={hasValidMultiInvestment}
          appliedBonus={maxAppliedBonusForPlacedMulti}
        >
          {isEditable && (
            <InvestmentsWrapperStyled data-testid='Multi.Investment'>
              {multiInvestment.isBonusBet ? (
                <BonusBetInvestment
                  id='Multi'
                  investmentType='Multi'
                  value={multiInvestment.value}
                  label='Cost'
                  display='stacked'
                  isEditable
                  selectedCampaign={campaigns.find(
                    campaign => campaign.id === multiInvestment.bonusBetId
                  )}
                />
              ) : (
                <Investment
                  isActive={activeInvestment?.investmentType === 'Multi'}
                  itemId='Multi'
                  investmentType='Multi'
                  label='Cost'
                  isEditable
                  isDisabled={isDisabled}
                  value={multiInvestment.value}
                  totalBonusValue={betSlipMultiInvestment.value}
                />
              )}

              <Investment
                isActive={activeInvestment?.investmentType === 'MultiReverseStake'}
                itemId='MultiReverseStake'
                investmentType='MultiReverseStake'
                label='Potential Payout'
                isEditable={!multiInvestment.bonusBetId}
                isDisabled={isDisabled}
                value={calculateMultiProjectedPay(selectedMultiItems, multiInvestment, true)}
              />
            </InvestmentsWrapperStyled>
          )}
        </MultiItems>

        {isBonusFundsAvailable && (
          <BonusBetAndCash
            id='Multi'
            bettingType='MULTI-BETTING-TYPE'
            multiInvestment={multiInvestment}
            isMulti
          />
        )}

        {workflowStatus !== 'proposed' && (
          <BetCard.Footer
            leftSection={receipt ? <ReceiptNumber ticketNumber={receipt.ticketNumber} /> : null}
            rightSection={
              <ShareMyBet
                items={selectedMultiItems}
                isBetPlaced={!!receipt}
                multiReceipt={receipt}
                ticketSequence={receipt?.ticketNumber}
              />
            }
            expandableText={shouldRenderMultiCombosInputs ? 'Combo Multis' : undefined}
            expandableContent={
              shouldRenderMultiCombosInputs ? (
                <ComboMultis isDisabled={isDisabled} selectedMultiItems={selectedMultiItems} />
              ) : null
            }
          />
        )}
      </Card>
    </Accordion>
  )
})

Multi.displayName = 'Multi'
